import { Observable, of, defer, merge } from "rxjs"
import { switchMap, catchError, filter, delayWhen, map, pairwise, takeUntil } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import type { EpicDependencies } from "../../../rxjs/configureEpics"
import { getHttpHeaders } from "../../../auth/selectors"
import { ACCOUNT_SELECTED, REFETCH_DATA } from "../../../auth/actions"
import {
    createLoadingEnded,
    createLoadingStarted,
    SET_FILTER_VALUE,
    SET_FILTERS,
    SET_SPECIAL_PRICING_REPORT_DEFAULT_FILTER,
    setSpecialPricingReportDefaultFilter,
} from "../../../filters/actions"
import { waitForAccountSelected, waitForGlobalFilterSet } from "../customers/customersTable/epics"
import {
    CLEAR_SPECIAL_PRICING_REPORT_DATA,
    clearSpecialPricingReportData,
    FETCH_SPECIAL_PRICINGS_EMPLOYEE_REPORT_REQUESTED,
    FETCH_SPECIAL_PRICINGS_LOG_REPORT_REQUESTED,
    FETCH_SPECIAL_PRICINGS_SUMMARY_REPORT_REQUESTED,
    fetchSpecialPricingsEmployeeReportFailure,
    fetchSpecialPricingsEmployeeReportRequested,
    fetchSpecialPricingsEmployeeReportSuccess,
    fetchSpecialPricingsLogReportFailure,
    fetchSpecialPricingsLogReportRequested,
    fetchSpecialPricingsLogReportSuccess,
    fetchSpecialPricingsSummaryReportFailure,
    fetchSpecialPricingsSummaryReportRequested,
    fetchSpecialPricingsSummaryReportSuccess,
    SPECIAL_PRICING_REPORT_AGENDA_MOUNTED,
    specialPricingReportAgendaMounted,
} from "./actions"
import { GLOBAL_FILTER_IDENTIFIER } from "../../../filters/reducer"
import { areAnyLocationsSelected } from "../../../filters/selectors"
import {
    fetchSpecialPricingByEmployeeReport,
    fetchSpecialPricingLogReport,
    fetchSpecialPricingSummaryReport,
} from "./request"
import { isSpecialPricingReportAgendaOpened } from "../../../data/selectors"
import { getSpecialPricingLogReportQueryApiParam } from "./selectors"
import { Action, StateObject } from "../../../redux/types"
import { URL_CHANGED } from "../../../router/actions"
import { getCurrentPathQuery } from "../../../router/selectors"
import { getReportsStateObject } from "../selectors"
import { SpecialPricingReportTabsEnum } from "./SpecialPricingReportTabsContainer"

const getActiveTab = (state: StateObject) => getCurrentPathQuery(state).tab

const fetchActiveTabReportRequested = (state: StateObject) => {
    switch (getActiveTab(state)) {
        case SpecialPricingReportTabsEnum.Employee:
            return fetchSpecialPricingsEmployeeReportRequested()
        case SpecialPricingReportTabsEnum.Log:
            return fetchSpecialPricingsLogReportRequested()
        default:
            return fetchSpecialPricingsSummaryReportRequested()
    }
}

const isActiveTabReportLoaded = (state: StateObject): boolean => {
    const reports = getReportsStateObject(state)
    switch (getActiveTab(state)) {
        case SpecialPricingReportTabsEnum.Employee:
            return reports.specialPricingByEmployee !== null
        case SpecialPricingReportTabsEnum.Log:
            return reports.specialPricingLogs !== null
        default:
            return reports.specialPricingSummary !== null
    }
}

export const specialPricingReportAgendaMountedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SPECIAL_PRICING_REPORT_AGENDA_MOUNTED),
        switchMap(() => {
            return [clearSpecialPricingReportData(), setSpecialPricingReportDefaultFilter()]
        })
    )

export const fetchSpecialPricingsReportOnAccountSwitch = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SELECTED),
        filter(() => isSpecialPricingReportAgendaOpened(deps.getState())),
        switchMap(() => [specialPricingReportAgendaMounted()])
    )

export const fetchSpecialPricingsReportOnTabSwitch = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(URL_CHANGED),
        map(() => ({
            isOpened: isSpecialPricingReportAgendaOpened(deps.getState()),
            tab: getActiveTab(deps.getState()),
        })),
        pairwise(),
        filter(([previous, current]) => previous.isOpened && current.isOpened && previous.tab !== current.tab),
        filter(() => !isActiveTabReportLoaded(deps.getState())),
        switchMap(() => [fetchActiveTabReportRequested(deps.getState())])
    )

export const specialPricingsReportDefaultFilterChangedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SET_SPECIAL_PRICING_REPORT_DEFAULT_FILTER),
        filter(() => isSpecialPricingReportAgendaOpened(deps.getState())),
        delayWhen(action => waitForGlobalFilterSet(actions$, deps.getState(), action)),
        switchMap(() => {
            return [clearSpecialPricingReportData(), fetchActiveTabReportRequested(deps.getState())]
        })
    )

export const salesByItemsReportFilterChanged = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    merge(
        actions$.pipe(
            ofType(SET_FILTERS, SET_FILTER_VALUE),
            filter(
                (action: Action) =>
                    action.payload.identifier === "specialPricings" ||
                    action.payload.identifier === GLOBAL_FILTER_IDENTIFIER
            )
        ),
        actions$.pipe(ofType(REFETCH_DATA))
    ).pipe(
        filter(() => isSpecialPricingReportAgendaOpened(deps.getState())),
        switchMap(() => {
            return [clearSpecialPricingReportData(), fetchActiveTabReportRequested(deps.getState())]
        })
    )

export const specialPricingsReportLoadingStartedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_SPECIAL_PRICINGS_LOG_REPORT_REQUESTED),
        filter(() => isSpecialPricingReportAgendaOpened(deps.getState())),
        switchMap(() => [createLoadingStarted("special-pricing-log-report")])
    )

export const fetchSpecialPricingLogReportRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_SPECIAL_PRICINGS_LOG_REPORT_REQUESTED),
        filter(() => isSpecialPricingReportAgendaOpened(deps.getState())),
        delayWhen((action: Action) => waitForAccountSelected(actions$, deps.getState(), action)),
        switchMap(() => {
            if (areAnyLocationsSelected(deps.getState())) {
                const params = getSpecialPricingLogReportQueryApiParam(deps.getState())
                return defer(() => fetchSpecialPricingLogReport(getHttpHeaders(deps.getState()), params)).pipe(
                    switchMap(result => {
                        if (result) {
                            return [
                                createLoadingEnded("special-pricing-log-report"),
                                fetchSpecialPricingsLogReportSuccess(result),
                            ]
                        }

                        return [
                            fetchSpecialPricingsLogReportFailure(),
                            createLoadingEnded("special-pricing-log-report"),
                        ]
                    }),
                    catchError(() => of(createLoadingEnded("special-pricing-log-report"))),
                    takeUntil(actions$.pipe(ofType(CLEAR_SPECIAL_PRICING_REPORT_DATA)))
                )
            }

            return [clearSpecialPricingReportData(), createLoadingEnded("special-pricing-log-report")]
        })
    )

export const specialPricingsSummaryReportLoadingStartedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_SPECIAL_PRICINGS_SUMMARY_REPORT_REQUESTED),
        filter(() => isSpecialPricingReportAgendaOpened(deps.getState())),
        switchMap(() => [createLoadingStarted("special-pricing-summary-report")])
    )

export const fetchSpecialPricingSummaryReportRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_SPECIAL_PRICINGS_SUMMARY_REPORT_REQUESTED),
        filter(() => isSpecialPricingReportAgendaOpened(deps.getState())),
        delayWhen((action: Action) => waitForAccountSelected(actions$, deps.getState(), action)),
        switchMap(() => {
            if (areAnyLocationsSelected(deps.getState())) {
                const params = getSpecialPricingLogReportQueryApiParam(deps.getState())
                return defer(() => fetchSpecialPricingSummaryReport(getHttpHeaders(deps.getState()), params)).pipe(
                    switchMap((result): any => {
                        if (result) {
                            return [
                                createLoadingEnded("special-pricing-summary-report"),
                                fetchSpecialPricingsSummaryReportSuccess(result),
                            ]
                        }

                        return [
                            fetchSpecialPricingsSummaryReportFailure(),
                            createLoadingEnded("special-pricing-summary-report"),
                        ]
                    }),
                    catchError(() => of(createLoadingEnded("special-pricing-summary-report"))),
                    takeUntil(actions$.pipe(ofType(CLEAR_SPECIAL_PRICING_REPORT_DATA)))
                )
            }

            return [clearSpecialPricingReportData(), createLoadingEnded("special-pricing-summary-report")]
        })
    )

export const specialPricingsByEmployeeReportLoadingStartedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_SPECIAL_PRICINGS_EMPLOYEE_REPORT_REQUESTED),
        filter(() => isSpecialPricingReportAgendaOpened(deps.getState())),
        switchMap(() => [createLoadingStarted("special-pricing-employee-report")])
    )

export const fetchSpecialPricingByEmployeeReportRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_SPECIAL_PRICINGS_EMPLOYEE_REPORT_REQUESTED),
        filter(() => isSpecialPricingReportAgendaOpened(deps.getState())),
        delayWhen(action => waitForAccountSelected(actions$, deps.getState(), action)),
        switchMap(() => {
            if (areAnyLocationsSelected(deps.getState())) {
                const params = getSpecialPricingLogReportQueryApiParam(deps.getState())
                return defer(() => fetchSpecialPricingByEmployeeReport(getHttpHeaders(deps.getState()), params)).pipe(
                    switchMap((result): any => {
                        if (result) {
                            return [
                                createLoadingEnded("special-pricing-employee-report"),
                                fetchSpecialPricingsEmployeeReportSuccess(result),
                            ]
                        }

                        return [
                            fetchSpecialPricingsEmployeeReportFailure(),
                            createLoadingEnded("special-pricing-employee-report"),
                        ]
                    }),
                    catchError(() => of(createLoadingEnded("special-pricing-employee-report"))),
                    takeUntil(actions$.pipe(ofType(CLEAR_SPECIAL_PRICING_REPORT_DATA)))
                )
            }

            return [clearSpecialPricingReportData(), createLoadingEnded("special-pricing-employee-report")]
        })
    )
export default [
    specialPricingReportAgendaMountedEpics,
    specialPricingsReportDefaultFilterChangedEpics,
    specialPricingsReportLoadingStartedEpics,
    fetchSpecialPricingsReportOnAccountSwitch,
    fetchSpecialPricingsReportOnTabSwitch,
    salesByItemsReportFilterChanged,
    fetchSpecialPricingLogReportRequestedEpics,
    specialPricingsSummaryReportLoadingStartedEpics,
    fetchSpecialPricingSummaryReportRequestedEpics,
    specialPricingsByEmployeeReportLoadingStartedEpics,
    fetchSpecialPricingByEmployeeReportRequestedEpics,
]
